import React, { useState } from 'react';
import { authService, readAuthError } from '@tl4k/frontend-shared';

interface AdminLoginProps {
  onLoginSuccess: () => void;
  onLoginRequest?: () => void;
  /** Contenuto opzionale da mostrare prima del titolo (es. logo/icona app). */
  logoSlot?: React.ReactNode;
  /** Titolo del pannello. Default: "Accesso Area Admin". */
  title?: string;
  /** Descrizione sotto il titolo. Default: testo admin generico. */
  description?: string;
  /** Classe CSS aggiuntiva per il pulsante di login. Default: blu. */
  buttonClassName?: string;
  /** URL di ritorno dopo OAuth. Per app dual-entry (es. disegna) passare l'URL dell'admin HTML. */
  loginReturnTo?: string;
}

const AdminLogin: React.FC<AdminLoginProps> = ({
  onLoginSuccess: _onLoginSuccess,
  onLoginRequest,
  logoSlot,
  title = 'Accesso Area Admin',
  description = 'Esegui il login con il tuo account Google per accedere al pannello di amministrazione.',
  buttonClassName = 'bg-blue-600 hover:bg-blue-700',
  loginReturnTo,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  // WP-03: auth-service redirige qui con ?auth_error=account_not_enabled quando l'allowlist rifiuta il primo login.
  const [authError] = useState<string | null>(() => readAuthError());

  const handleLogin = () => {
    setIsLoading(true);
    onLoginRequest?.();
    authService.login(loginReturnTo);
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center bg-slate-100 p-8">
      <div className="bg-white p-12 rounded-2xl shadow-xl max-w-md text-center">
        {logoSlot}
        <h2 className="text-3xl font-bold text-slate-800 mb-4">{title}</h2>
        <p className="text-slate-500 mb-8">{description}</p>

        {authError && (
          <p role="alert" className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
            {authError}
          </p>
        )}

        <button
          onClick={handleLogin}
          disabled={isLoading}
          className={`${buttonClassName} text-white font-semibold py-3 px-8 rounded-lg shadow-md transition-all active:scale-95 disabled:bg-slate-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 w-full`}
        >
          {isLoading ? (
            <>
              <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
              <span>Reindirizzamento in corso…</span>
            </>
          ) : (
            <>
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              <span>Accedi con Google</span>
            </>
          )}
        </button>

        {/* Dev Login: localhost o VITE_ENABLE_DEV_LOGIN; in produzione solo Google */}
        {authService.getShowDevLogin() && (
          <div className="mt-8 border-t pt-6">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 text-center">
              Profili di Sviluppo
            </p>
            <div className="grid grid-cols-1 gap-2">
              <button
                onClick={() => authService.devLogin('superadmin@dev.local')}
                className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-2 px-4 rounded transition-colors border border-slate-200"
              >
                Accedi come super amministratore
              </button>
              <button
                onClick={() => authService.devLogin('admin@dev.local')}
                className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-2 px-4 rounded transition-colors border border-slate-200"
              >
                Login come School Admin
              </button>
              <button
                onClick={() => authService.devLogin('teacher@dev.local')}
                className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-2 px-4 rounded transition-colors border border-slate-200"
              >
                Accedi come insegnante
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLogin;

